import React, { useState } from "react";
import { Search } from "lucide-react";
import { asText, EmptyRow, formatCompact, formatCurrency, formatRoas, LoadingRow, StatusBadge, unwrapValue, useCxoResource } from "../shared";

const STATUS_OPTIONS = ["All", "ACTIVE", "PAUSED", "LEARNING"];
const PLACEMENT_OPTIONS = ["All", "feed", "stories", "reels", "explore"];

const CampaignAdsetsTab: React.FC<{ campaignId: string; baseParams: Record<string, any>; onOpenAdset: (adsetId: string) => void }> = ({ campaignId, baseParams, onOpenAdset }) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [placement, setPlacement] = useState("All");
  const params = { ...baseParams, sort_by: "spend", order: "desc", limit: "25", offset: "0", ...(search ? { q: search } : {}), ...(status !== "All" ? { status } : {}), ...(placement !== "All" ? { placement } : {}) };
  const { data, loading } = useCxoResource<any>("/cxo-dashboard/campaigns", `${campaignId}/adsets`, params, !!campaignId);
  const items: any[] = Array.isArray(data?.items) ? data.items : [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ad sets..." className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200">
          {STATUS_OPTIONS.map((o) => (
            <option key={o} value={o}>
              Status: {o}
            </option>
          ))}
        </select>
        <select value={placement} onChange={(e) => setPlacement(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200">
          {PLACEMENT_OPTIONS.map((o) => (
            <option key={o} value={o}>
              Placement: {o}
            </option>
          ))}
        </select>
      </div>

      {loading && items.length === 0 ? (
        <LoadingRow label="Loading ad sets…" />
      ) : items.length === 0 ? (
        <EmptyRow label="No ad sets match these filters." />
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                <th className="pb-2 pl-1">AD SET NAME</th>
                <th className="pb-2">STATUS</th>
                <th className="pb-2">AUDIENCE</th>
                <th className="pb-2">SPEND</th>
                <th className="pb-2">IMPRESSIONS</th>
                <th className="pb-2">REACH</th>
                <th className="pb-2">RESULTS</th>
                <th className="pb-2">COST PER RESULT</th>
                <th className="pb-2 pr-1 text-right">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.adset_id || i} className="border-t border-gray-50 dark:border-gray-800">
                  <td className="py-2.5 pl-1 whitespace-nowrap">
                    <button type="button" onClick={() => it.adset_id && onOpenAdset(it.adset_id)} className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline text-left">
                      {asText(it.name)}
                    </button>
                  </td>
                  <td className="py-2.5">
                    <StatusBadge status={it.status ?? it.learning_stage} />
                  </td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300 max-w-[200px] truncate">{asText(it.audience?.summary ?? it.audience)}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.spend ?? it.metrics?.spend), "₦0.00")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.impressions ?? it.metrics?.impressions), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.reach ?? it.metrics?.reach), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.results ?? it.metrics?.results), "0")}</td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.cost_per_result ?? it.metrics?.cost_per_result), "₦0.00")}</td>
                  <td className="py-2.5 pr-1 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatRoas(unwrapValue(it.roas ?? it.metrics?.roas), "N/A")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CampaignAdsetsTab;
